import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Checkbox, Button, Card, Spin } from "antd";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import css from "./PaymentSettingsComponent.module.css";
import { appSettingsActions } from "../../../store/slices/settings-slices";

const TABBY_ENV_ENABLED = process.env.REACT_APP_ENABLE_TABBY === "true";

const ALL_METHODS = [
    {
        key: "Card",
        label: "Pay by Card",
        description: "Secure payment via card",
        icon: "💳",
    },
    {
        key: "Transfer",
        label: "Bank Transfer",
        description: "Transfer to our bank account",
        icon: "🏦",
    },
    {
        key: "Cash",
        label: "Pay by Cash",
        description: "Pay to courier upon delivery",
        icon: "💵",
    },
    ...(TABBY_ENV_ENABLED
        ? [{
            key: "Tabby",
            label: "Pay with Tabby",
            description: "Split into interest-free payments",
            icon: null,
        }]
        : []),
];

const PaymentSettingsComponent = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { enabledPaymentMethods, status } = useSelector((s) => s.appSettings);
    const isLoading = status === "loading";

    const [localEnabled, setLocalEnabled] = useState(enabledPaymentMethods);

    useEffect(() => {
        setLocalEnabled(enabledPaymentMethods);
    }, [enabledPaymentMethods]);

    const isDirty =
        localEnabled.length !== enabledPaymentMethods.length ||
        localEnabled.some((k) => !enabledPaymentMethods.includes(k));

    const toggle = (key) => {
        setLocalEnabled((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );
    };

    const handleSave = async () => {
        try {
            await dispatch(
                appSettingsActions.updateAppSettings({ enabledPaymentMethods: localEnabled })
            ).unwrap();
            toast.success(t("Payment settings saved successfully"));
        } catch {
            toast.error(t("Failed to save payment settings"));
        }
    };

    const renderRow = (method, disabled = false) => {
        const checked = localEnabled.includes(method.key);
        return (
            <div
                key={method.key}
                className={`${css.row} ${disabled ? css.rowDisabled : ""}`}
                onClick={() => !disabled && toggle(method.key)}
            >
                <Checkbox
                    checked={checked}
                    disabled={disabled}
                    onChange={() => toggle(method.key)}
                    onClick={(e) => e.stopPropagation()}
                />
                <div className={css.rowIcon}>
                    {method.icon ? (
                        <span className={css.emoji}>{method.icon}</span>
                    ) : (
                        <span className={css.tabbyIcon}>t</span>
                    )}
                </div>
                <div className={css.rowText}>
                    <span className={css.rowLabel}>{method.label}</span>
                    <span className={css.rowDesc}>{method.description}</span>
                </div>
            </div>
        );
    };

    return (
        <section className={css.wrap}>
            <Spin spinning={isLoading}>
                <Card title={t("Payment Methods")} className={css.card}>
                    <p className={css.hint}>
                        {t("Choose which payment methods are available to customers at checkout.")}
                    </p>

                    <div className={css.list}>
                        {ALL_METHODS.map((m) => renderRow(m))}
                    </div>

                    <div className={css.actions}>
                        <Button type="primary" onClick={handleSave} loading={isLoading} disabled={!isDirty}>
                            {t("Save payment settings")}
                        </Button>
                    </div>
                </Card>
            </Spin>
        </section>
    );
};

export { PaymentSettingsComponent };
